import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { subscriptionService } from '@/firebase/subscriptionService';
import { organizationService } from '@/firebase/organizationService';
import { db } from '@/firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const SubscriptionDiagnostics: React.FC = () => {
  const { user } = useAuth();
  const { currentOrganization, organizations } = useOrganization();
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  const runDiagnostics = async () => {
    setIsLoading(true);
    setMessage('');
    const results: any = {
      user: {
        id: user?.id,
        email: user?.email,
        organizationId: user?.organizationId,
      },
      currentOrganization: {
        id: currentOrganization?.id,
        name: currentOrganization?.name,
        ownerId: currentOrganization?.ownerId,
        stripeCustomerId: (currentOrganization as any)?.stripeCustomerId,
        subscription: (currentOrganization as any)?.subscription,
      },
      allOrganizations: [],
    };

    try {
      // Check all organizations
      if (user?.id) {
        const orgsResult = await organizationService.getOrganizationsByOwner(user.id);
        if (orgsResult.success && orgsResult.data) {
          for (const org of orgsResult.data) {
            const orgRef = doc(db, 'organizations', org.id!);
            const orgSnap = await getDoc(orgRef);
            const orgData = orgSnap.data();
            
            const subResult = await subscriptionService.getSubscription(org.id!, user.id);
            
            results.allOrganizations.push({
              id: org.id,
              name: org.name,
              ownerId: org.ownerId,
              stripeCustomerId: orgData?.stripeCustomerId,
              hasSubscription: !!orgData?.subscription,
              subscription: orgData?.subscription,
              subscriptionServiceResult: subResult,
            });
          }
        }
      }
      
      setDiagnostics(results);
    } catch (error: any) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fixSubscriptionAssociation = async (targetOrgId: string, subscriptionId: string) => {
    setIsLoading(true);
    setMessage('');
    
    try {
      // Get subscription from Stripe (via the sync function)
      const functionsUrl = `https://us-central1-lemur-476302.cloudfunctions.net`;
      const response = await fetch(`${functionsUrl}/syncSubscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ organizationId: targetOrgId, subscriptionId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to sync subscription');
      }

      setMessage('✅ Subscription successfully moved to the correct organization!');
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Subscription Diagnostics</h2>
        <button
          onClick={runDiagnostics}
          disabled={isLoading}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {isLoading ? 'Running...' : 'Run Diagnostics'}
        </button>
      </div>

      {message && (
        <div className={`p-3 rounded-lg ${
          message.includes('✅') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {message}
        </div>
      )}

      {diagnostics && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg border">
            <h3 className="font-semibold mb-2">Current Organization</h3>
            <pre className="text-xs overflow-auto bg-gray-50 p-2 rounded">
              {JSON.stringify(diagnostics.currentOrganization, null, 2)}
            </pre>
          </div>

          <div className="bg-white p-4 rounded-lg border">
            <h3 className="font-semibold mb-2">All Your Organizations</h3>
            {diagnostics.allOrganizations.map((org: any, idx: number) => (
              <div key={idx} className="mb-4 p-3 bg-gray-50 rounded border">
                <div className="font-medium mb-2">
                  {org.name} ({org.id})
                </div>
                {org.hasSubscription ? (
                  <div className="space-y-1">
                    <div className="text-sm">
                      <span className="font-medium">Subscription:</span> {org.subscription?.plan} - {org.subscription?.status}
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Stripe Subscription ID:</span> {org.subscription?.stripeSubscriptionId}
                    </div>
                    {org.id !== diagnostics.currentOrganization?.id && (
                      <button
                        onClick={() => {
                          if (org.subscription?.stripeSubscriptionId) {
                            if (confirm(`Move subscription to organization "${org.name}"?`)) {
                              fixSubscriptionAssociation(org.id, org.subscription.stripeSubscriptionId);
                            }
                          }
                        }}
                        className="mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        Move Subscription Here
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">No subscription</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionDiagnostics;

